import Vue from "vue";
import VueRouter, { NavigationGuardNext, Route } from "vue-router";
import HomeView from "../views/home/HomeView.vue";
import ClientView from "@/views/client/ClientView.vue";
import ClientFormView from "@/views/client/ClientFormView.vue";
import ClientBulkImportView from "@/views/client/ClientBulkImportView.vue";
import SalesView from "@/views/sales/SalesView.vue";
import OnboardingFormView from "@/views/onboarding/OnboardingFormView.vue";
import OnboardingChecklistView from "@/views/onboarding/OnboardingChecklistView.vue";
import Login from "../views/login/Login.vue";
import ForgotPassword from "../views/login/ForgotPassword.vue";
import PartnerDetailView from "@/views/partner/PartnerDetail.vue";
import SettingsVue from "@/views/settings/Settings.vue";
import IndicationsView from "@/views/indications/IndicationsView.vue";
import AccountCancellationRequest from "@/views/account/AccountCancellationRequest.vue";
import AccountCancellationConfirmation from "@/views/account/AccountCancellationConfirmation.vue";
import RegistrateContinueView from "@/views/registrate/RegistrateContinueView.vue";
import PdvView from "@/views/pdv/PdvView.vue";
import ProductListView from "@/views/pdv/ProductListView.vue";
import ProductFormView from "@/views/pdv/ProductFormView.vue";
import PdvDashboardView from "@/views/pdv/PdvDashboardView.vue";
import FinanceView from "@/views/finance/FinanceView.vue";
import store from "@/store";

Vue.use(VueRouter);

const PUBLIC_ROUTES = new Set([
  "login",
  "forgotPassword",
  "onboarding",
  "registrateContinue",
  "accountCancellationRequest",
  "accountCancellationConfirmation",
]);

const getAccessToken = (): string | false => {
  try {
    const userData = JSON.parse(String(localStorage.getItem("tokens")));
    return userData?.accessToken || false;
  } catch {
    return false;
  }
};

const authGuard = async (
  to: Route,
  _from: Route,
  next: NavigationGuardNext,
) => {
  const token = getAccessToken();

  if (PUBLIC_ROUTES.has(String(to.name))) {
    if (token && (to.name === "login" || to.name === "forgotPassword")) {
      next("/");
      return;
    }
    next();
    return;
  }

  if (!token) {
    localStorage.setItem("redirectAfterLogin", to.fullPath);
    next("/login");
    return;
  }

  if (!store.state.partnerSession) {
    try {
      await store.dispatch("loadSession");
    } catch {
      store.dispatch("clearSession");
      localStorage.removeItem("tokens");
      next("/login");
      return;
    }
  }

  next();
};

const routes = [
  {
    path: "/login",
    name: "login",
    component: Login,
  },
  {
    path: "/onboarding",
    name: "onboarding",
    component: OnboardingFormView,
  },
  {
    path: "/registrate/continue/:partnerId",
    name: "registrateContinue",
    component: RegistrateContinueView,
  },
  {
    path: "/onboarding/checklist",
    name: "onboardingChecklist",
    component: OnboardingChecklistView,
  },
  {
    path: "/forgot-password",
    name: "forgotPassword",
    component: ForgotPassword,
  },
  {
    path: "/clients",
    name: "clients",
    component: ClientView,
  },
  {
    path: "/clients/import",
    name: "clientsImport",
    component: ClientBulkImportView,
  },
  {
    path: "/client/form/",
    name: "clientForm",
    component: ClientFormView,
  },
  {
    path: "/client/form/:id",
    name: "clientDetail",
    component: ClientFormView,
  },
  {
    path: "/sales",
    name: "sales",
    component: SalesView,
  },
  {
    path: "/my-data",
    name: "myData",
    component: PartnerDetailView,
  },
  {
    path: "/sales/new",
    redirect: "/pdv",
  },
  {
    path: "/sales-online/new",
    redirect: "/pdv",
  },
  {
    path: "/settings",
    name: "settings",
    component: SettingsVue,
  },
  {
    path: "/indications",
    name: "indications",
    component: IndicationsView,
  },
  {
    path: "/account/cancellation-request",
    name: "accountCancellationRequest",
    component: AccountCancellationRequest,
  },
  {
    path: "/account/cancellation-confirmation",
    name: "accountCancellationConfirmation",
    component: AccountCancellationConfirmation,
  },
  {
    path: "/pdv",
    name: "pdv",
    component: PdvView,
  },
  {
    path: "/produtos",
    name: "produtos",
    component: ProductListView,
  },
  {
    path: "/produtos/new",
    name: "produtoNew",
    component: ProductFormView,
  },
  {
    path: "/produtos/:id",
    name: "produtoEdit",
    component: ProductFormView,
  },
  {
    path: "/pdv/sales",
    redirect: "/sales",
  },
  {
    path: "/financeiro",
    name: "financeiro",
    component: FinanceView,
  },
  {
    path: "/pdv/dashboard",
    name: "pdvDashboard",
    component: PdvDashboardView,
  },
  {
    path: "/pdv/products",
    redirect: "/produtos",
  },
  {
    path: "/pdv/products/new",
    redirect: "/produtos/new",
  },
  {
    path: "/pdv/products/:id",
    redirect: (to: Route) => `/produtos/${to.params.id}`,
  },
  {
    path: "/pdv/categories",
    redirect: { path: "/produtos", query: { tab: "categories" } },
  },
  {
    path: "/estoque",
    redirect: { path: "/produtos", query: { tab: "estoque" } },
  },
  {
    path: "/",
    name: "home",
    component: HomeView,
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach(authGuard);

export default router;
